import { v4 as uuidv4 } from "uuid";

import { getConsent } from "../helpers/analyticsHelpers";
import { getNested } from "../helpers/commonUtils";
import { loadAsset } from "../helpers/scripts";
import { PlayerStoreProperties } from "../store/store";
import { PlayerEmbedParams } from "../types/common";

export const importMediaSDK = () =>
  import(/* webpackChunkName: "MediaSDK" */ "../../../lib/MediaSDK.min.js");

const getGetAppMeasurementMethod = () =>
  getNested(["adobe", "getAppMeasurement"], window.yleAnalytics);

export const resolveAppMeasurement = async () =>
  getGetAppMeasurementMethod() ||
  loadAsset({ url: __ANALYTICS_SDK }).then(getGetAppMeasurementMethod);

export const getStreamType = (isLive, isAudio, streamType) =>
  isLive ? streamType.Live : isAudio ? streamType.AOD : streamType.VOD;

const isLapset = () =>
  typeof window !== "undefined" &&
  window.location.pathname.startsWith("/lapset");

const resolveVrsProduct = (product?: unknown): string =>
  typeof product === "string"
    ? product
    : isLapset()
      ? "lastenareena"
      : "areena";

const getYlePackageId = (id: string): string | undefined => {
  const getPackageRefererMethod = getNested(
    ["browsingHistory", "getPackageReferer"],
    window.yleAnalytics
  );
  const packageId = getPackageRefererMethod?.(id);
  return packageId || undefined;
};

const getEmbedUrl = (): string => {
  const getUrlMethod = getNested(["getUrl"], window.yleAnalytics);
  return getUrlMethod ? getUrlMethod() : window.location.href;
};

export const getAreenaAbString = (): string | undefined => {
  const getTestBucketsMethod = getNested(
    ["areenaAb", "getTestBuckets"],
    window
  );
  return getTestBucketsMethod?.();
};

const resolveMediaType = (contentType: string): "RadioContent" | "TVContent" =>
  contentType === "AudioObject" ? "RadioContent" : "TVContent";

const removeUndefinedValues = (
  context: Record<string, string | undefined>
): Record<string, string> =>
  Object.entries(context).reduce<Record<string, string>>(
    (acc, [key, value]) => {
      if (value !== undefined) {
        acc[key] = value;
      }
      return acc;
    },
    {}
  );

export const parseContext = ({
  dvrWindowInSeconds,
  embedParameters,
  isMobile,
  programId,
  state
}: {
  dvrWindowInSeconds?: number;
  embedParameters: PlayerEmbedParams;
  isMobile: boolean;
  programId: string;
  state: PlayerStoreProperties;
}): Record<string, string> => {
  const consent = getConsent();
  const context = {
    player_autoplay: embedParameters.autoplay ? "true" : "false",
    player_dvr_window: dvrWindowInSeconds
      ? dvrWindowInSeconds.toString()
      : undefined,
    player_loop: embedParameters.loop ? "true" : "false",
    userconsent: consent.userconsent,
    "yle.vrsContent": state.isAudio ? "audio" : "video",
    "yle.vrsDevice": "browser",
    "yle.vrsPlatform": isMobile ? "mobile" : "desktop",
    "yle.vrsProduct": resolveVrsProduct(
      embedParameters.analyticsContext?.product
    ),
    yle_ab_string: getAreenaAbString(),
    yle_audio_language: embedParameters.language,
    yle_country_code: state.geolocation.countryCode,
    yle_id: programId,
    yle_interaction: state.isContinuingWatching
      ? "player.continue.watching"
      : undefined,
    yle_media_id: state.mediaId,
    yle_media_type: resolveMediaType(state.contentType),
    yle_package_id: getYlePackageId(programId),
    yle_player_embed_url: getEmbedUrl(),
    yle_publication_event_id: state.publicationId,
    yle_publication_event_type: state.publicationType,
    yle_rec: window.yleAnalytics?.getYleRec?.(),
    yle_selva: window.yleAnalytics?.getYleSelvaId?.(),
    yle_service_id: state.serviceId,
    yle_sk: state.loginData.userId,
    yle_vsid: uuidv4(),
    yleconsent: consent.yleconsent,
    ...embedParameters.analyticsContext
  };

  return removeUndefinedValues(context);
};
